import * as React from "react";
import { useState, useEffect } from "react";
import Button from "./button";
import { isSystemIOS } from "@/utils/common";
import { videoPack } from "@/components/utils/videoPack";
import { getFileStorageUrl, getFileStorageUrlSync } from "@/utils/storage";
import LevelProgressBar from "./level-progress-bar";
import { LevelUpMessage } from "./level-up-message";
import { ItemDataType } from "@/types";
import {
  calcLevelByExperience,
  getExperienceProgress,
  getExperienceToLevel,
} from "@/utils/experience";

interface NftSuccessMessageProps {
  avatar: string;
  index: number;
  className?: string;
  collectionName: string;
  videoUrl: string;
  mimeType: string;
  isSBT: boolean;
  itemData?: ItemDataType;
  walletAddress?: string;
  previousExperience: number;
  onClose: () => void;
  onClick: () => void;
}

export default function NftSuccessMessage({
  className,
  avatar,
  index,
  collectionName,
  videoUrl,
  mimeType,
  isSBT,
  walletAddress,
  itemData,
  previousExperience,
  onClose,
  onClick,
}: NftSuccessMessageProps) {
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
  const [avatagImg, setAvatarImg] = useState<string | null>("");
  const [showLevelUpMessage, setShowLevelUpMessage] = useState<boolean>(false);
  const [animationPhase, setAnimationPhase] = useState<"phase1" | "phase2">(
    "phase1"
  );

  const exp = itemData ? Number(itemData.experience) || 0 : 0;

  const calculatedCurrentExperience = Number(previousExperience) + Number(exp);
  const previousLevel = calcLevelByExperience(previousExperience);
  const currentLevel = calcLevelByExperience(calculatedCurrentExperience);
  const hasLeveledUp = previousLevel < currentLevel;

  const levelThreshold = hasLeveledUp
    ? getExperienceToLevel(previousLevel + 1)
    : getExperienceToLevel(currentLevel + 1);
  const phase1CurrentExp = hasLeveledUp
    ? levelThreshold - 1
    : calculatedCurrentExperience;
  const phase1XpGain = hasLeveledUp ? phase1CurrentExp - previousExperience : 0;

  const phase2CurrentExp = calculatedCurrentExperience;
  const phase2XpGain = exp;

  const displayCurrentExp = !hasLeveledUp
    ? calculatedCurrentExperience
    : hasLeveledUp && animationPhase === "phase1"
      ? phase1CurrentExp
      : phase2CurrentExp;

  const displayXpGain =
    exp === 0
      ? 0
      : !hasLeveledUp
        ? exp
        : hasLeveledUp && animationPhase === "phase1"
          ? phase1XpGain
          : phase2XpGain;

  useEffect(() => {
    setIsIOS(isSystemIOS());
    getFileStorageUrl(avatar)
      .then(res => setAvatarImg(res))
      .catch(console.error);
  }, []);

  const handleReach100 = () => {
    if (hasLeveledUp && animationPhase === "phase1") {
      setShowLevelUpMessage(true);
    }
  };

  const handleLevelUpMessageClick = () => {
    setShowLevelUpMessage(false);
    setAnimationPhase("phase2");
  };

  return (
    <>
      {showLevelUpMessage && (
        <LevelUpMessage
          level={currentLevel}
          onClick={handleLevelUpMessageClick}
        />
      )}

      <div
        className={`${className} ${showLevelUpMessage ? "blur-[4px] pointer-events-none" : "opacity-100 pointer-events-auto"}`}
      >
        <div className="relative">
          <header className="relative flex justify-center items-center pl-[20px] pr-[20px] pt-[12px] pb-[12px] bg-[#FE9400] ">
            <div className="text-[28px] text-center">
              <span>CONGRATULATION!</span>
            </div>
            <div
              className="absolute right-[20px] w-[21px] h-[29px] hover:cursor-pointer"
              onClick={onClose}
            >
              <img src="/assets/cancel.svg" alt="cancel" />
            </div>
          </header>
          <main className="flex flex-col justify-center items-center pt-[58px] pb-[30px] pl-[54px] pr-[54px] text-[24px] font-spline-sans-mono">
            <div className="w-[145px] aspect-square relative">
              {mimeType === "video/mp4" && videoUrl ? (
                videoPack(
                  isIOS,
                  getFileStorageUrlSync(videoUrl),
                  setIsVideoLoaded
                )
              ) : (
                <img src={getFileStorageUrlSync(avatar)} alt="avatar" />
              )}
              <div className="absolute top-0 left-0 w-[145px] aspect-square  flex justify-center items-center">
                <img src="/assets/avatar-bg.png" alt="index" />
              </div>
            </div>
            <p className="flex flex-col items-center pt-[28px] font-spline-sans-mono">
              <span>
                Your {isSBT ? "SBT" : "NFT"} #{index} from{" "}
              </span>
              <span>
                "<span className="text-[#0099FE]">{collectionName}</span>"
              </span>
              <span className="text-center">successfully created!</span>
            </p>

            {exp > 0 && (
              <div className="pt-[20px] w-full">
                <LevelProgressBar
                  currentExperience={displayCurrentExp}
                  hasLeveledUp={hasLeveledUp && animationPhase === "phase2"}
                  xpGain={displayXpGain}
                  onReach100={handleReach100}
                />
              </div>
            )}

            <div className="pt-[30px] w-full">
              <Button
                className="w-full custom-aspect-320-63 text-[24px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
                image="/assets/button.svg"
                text={`GO TO ${isSBT ? "SBT" : "NFT"}`}
                onClick={() => {
                  onClick();
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
